import React, { useRef } from 'react';
import styled from 'styled-components';

interface AnnouncementModalProps {
  isOpen: boolean;
  content: string;
  onClose: () => void;
  onDontShowToday: () => void;
  enableCountdown?: boolean;
}

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 16px;
  padding: 32px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  box-shadow: var(--modal-shadow-large);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateY(-50px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  :root:not([data-theme='light']) & {
    border: var(--modal-shell-border);
  }
`;

const ModalTitle = styled.h2`
  margin: 0 0 24px 0;
  color: var(--color-text-primary);
  font-size: 1.5rem;
  font-weight: 700;
  text-align: center;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 24px;
  color: var(--color-text-primary);
  font-size: 1rem;
  line-height: 1.6;
  word-break: break-word;
  text-align: center; /* 기본 중앙 정렬 */
  
  /* HTML 콘텐츠 스타일 - 정렬 스타일은 제외 */
  & * {
    max-width: 100%;
  }
  
  & p {
    margin: 0.5em 0;
  }
  
  & strong {
    font-weight: 700;
  }
  
  & em {
    font-style: italic;
  }
  
  & u {
    text-decoration: underline;
  }
  
  /* 링크 스타일 (버튼 형태) - 인라인 스타일을 덮기 위해 !important 사용 */
  & a {
    display: inline-block !important;
    padding: 12px 24px !important;
    border: 2px solid var(--color-border) !important;
    border-radius: 10px !important;
    font-size: 15px !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: all 0.2s ease !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
    text-decoration: none !important;
    margin: 4px !important;
  }

  /* 라이트 모드: 흰색 블럭 + 진한 회색 텍스트 */
  :root[data-theme='light'] & a {
    background: #ffffff !important;
    color: #2d2d2d !important;
  }

  :root[data-theme='light'] & a:hover {
    background: #f5f5f5 !important;
    border-color: #717171 !important;
    color: #1a1a1a !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12) !important;
  }

  :root[data-theme='light'] & a:active {
    background: #ebebeb !important;
  }

  /* 다크 모드 */
  :root:not([data-theme='light']) & a {
    background: rgba(255, 255, 255, 0.1) !important;
    color: #ffffff !important;
  }

  :root:not([data-theme='light']) & a:hover {
    background: rgba(255, 255, 255, 0.15) !important;
    border-color: rgba(255, 255, 255, 0.5) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
  }

  :root:not([data-theme='light']) & a:active {
    background: rgba(255, 255, 255, 0.08) !important;
  }

  & a:active {
    transform: scale(0.98);
  }

  & a:focus-visible {
    outline: 2px solid #000 !important;
    outline-offset: 2px !important;
  }
  
  /* 이미지 스타일 */
  & img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 8px auto;
    border-radius: 8px;
  }
  
  /* 인라인 스타일의 text-align 속성 보존 - !important로 우선순위 높임 */
  & [style*="text-align: left"],
  & [style*="text-align:left"],
  & [style*="text-align: left;"],
  & [style*="text-align:left;"] {
    text-align: left !important;
  }

  & [style*="text-align: center"],
  & [style*="text-align:center"],
  & [style*="text-align: center;"],
  & [style*="text-align:center;"] {
    text-align: center !important;
  }

  & [style*="text-align: right"],
  & [style*="text-align:right"],
  & [style*="text-align: right;"],
  & [style*="text-align:right;"] {
    text-align: right !important;
  }
  
  /* div, p 등 블록 요소의 정렬 지원 */
  & div[style*="text-align"],
  & p[style*="text-align"],
  & span[style*="text-align"],
  & h1[style*="text-align"],
  & h2[style*="text-align"],
  & h3[style*="text-align"],
  & h4[style*="text-align"],
  & h5[style*="text-align"],
  & h6[style*="text-align"] {
    display: block;
  }
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--color-panel);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--color-border);
    border-radius: 4px;
    
    &:hover {
      background: var(--color-border-strong);
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CloseButton = styled.button`
  background: var(--color-accent-strong);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease;
  position: relative;
  min-height: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;

  &:hover {
    background: var(--color-accent);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const AnnouncementModal: React.FC<AnnouncementModalProps> = ({
  isOpen,
  content,
  onClose,
  onDontShowToday,
  enableCountdown,
}) => {
  const modalBodyRef = useRef<HTMLDivElement>(null);
  void onDontShowToday;
  void enableCountdown;

  if (!isOpen) return null;

  return (
    <ModalOverlay isOpen={isOpen}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalTitle>공지사항</ModalTitle>
        <ModalBody
          ref={modalBodyRef}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: content || '<p>공지사항이 없습니다.</p>'
            }}
            style={{
              textAlign: 'center' // 기본 중앙 정렬 적용
            }}
          />
        </ModalBody>
        <ButtonContainer>
          <CloseButton onClick={onClose}>닫기</CloseButton>
          {/* 나중에 필요하면 주석 해제 */}
          {/* <DontShowTodayButton onClick={onDontShowToday}>
            오늘 하루 보지 않기
          </DontShowTodayButton> */}
        </ButtonContainer>
      </ModalContent>
    </ModalOverlay>
  );
};

export default AnnouncementModal;

